import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import LanguageSwitcher from '../components/LanguageSwitcher';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      toast.success(t('toast.loginSuccess'));
      navigate('/');
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6">
      <div className="absolute top-6 right-6">
        <LanguageSwitcher />
      </div>
      <div className="max-w-md w-full">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8">
          <h1 className="text-3xl font-black text-slate-900 mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {t('auth.welcomeBack')}
          </h1>
          <p className="text-slate-600 mb-6" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t('auth.signInSubtitle')}
          </p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-slate-700">{t('common.email')}</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">{t('common.password')}</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="••••••••"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-btn"
              disabled={loading}
              className="w-full bg-blue-900 text-white hover:bg-blue-800"
            >
              {loading ? t('auth.signingIn') : t('auth.signIn')}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            {t('auth.noAccount')}{' '}
            <Link to="/register" className="text-blue-900 font-bold hover:text-blue-800">
              {t('common.signUp')}
            </Link>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-sm text-slate-600 hover:text-slate-900">
            ← {t('common.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
